import {
  CALENDAR_RECURRENCE_SCHEDULE_TYPE,
  CalendarRecurrenceSchedule,
  VALID_SCHEDULE_TIME,
} from "./types";

export type MonthlySchedulePickerState = {
  days: number[];
  time: string;
};

type ScheduleEnvelope = Pick<
  CalendarRecurrenceSchedule,
  "startDate" | "endDate"
>;

export function monthlyPickerStateToSchedule(
  state: MonthlySchedulePickerState,
  envelope?: ScheduleEnvelope,
): CalendarRecurrenceSchedule {
  return {
    type: CALENDAR_RECURRENCE_SCHEDULE_TYPE,
    ...(envelope
      ? { startDate: envelope.startDate, endDate: envelope.endDate }
      : {}),
    schedule: {
      unit: "month",
      days: [...state.days].sort((a, b) => a - b),
      time: state.time,
      overflow: "last_day",
      timezone: "UTC",
    },
  };
}

export function calendarScheduleToMonthlyPickerState(
  schedule: CalendarRecurrenceSchedule,
): MonthlySchedulePickerState {
  return {
    days: [...schedule.schedule.days],
    time: schedule.schedule.time,
  };
}

export function isValidMonthlySchedule(
  schedule: CalendarRecurrenceSchedule,
): boolean {
  const { days, time, unit, overflow, timezone } = schedule.schedule;

  return (
    unit === "month" &&
    overflow === "last_day" &&
    timezone === "UTC" &&
    VALID_SCHEDULE_TIME.test(time) &&
    days.length > 0 &&
    new Set(days).size === days.length &&
    days.every((day) => Number.isInteger(day) && day >= 1 && day <= 31)
  );
}

function ordinal(day: number): string {
  const remainder = day % 100;
  if (remainder >= 11 && remainder <= 13) return `${day}th`;

  switch (day % 10) {
    case 1:
      return `${day}st`;
    case 2:
      return `${day}nd`;
    case 3:
      return `${day}rd`;
    default:
      return `${day}th`;
  }
}

function formatList(values: string[]): string {
  if (values.length === 1) return values[0]!;
  if (values.length === 2) return `${values[0]} and ${values[1]}`;
  return `${values.slice(0, -1).join(", ")}, and ${values.at(-1)}`;
}

function formatUtcTime(time: string): string {
  const [hours, minutes] = time.split(":").map(Number);
  const suffix = hours! >= 12 ? "PM" : "AM";
  const twelveHour = hours! % 12 || 12;
  return `${twelveHour}:${String(minutes).padStart(2, "0")} ${suffix}`;
}

export function formatMonthlyScheduleSummary(
  state: MonthlySchedulePickerState,
): string | null {
  if (state.days.length === 0 || !VALID_SCHEDULE_TIME.test(state.time)) {
    return null;
  }

  const days = [...state.days].sort((a, b) => a - b);
  const dayList = formatList(days.map(ordinal));
  const hasClampedDay = days.some((day) => day >= 29);
  const hasClampedDuplicates = days.filter((day) => day >= 28).length > 1;
  const clampNote = hasClampedDuplicates
    ? " (last day in shorter months; clamped duplicates collapse to one run)"
    : hasClampedDay
      ? " (last day in shorter months)"
      : "";

  return `Monthly on the ${dayList} at ${formatUtcTime(state.time)} UTC${clampNote}.`;
}
